import React from 'react';
import { motion } from 'framer-motion';
import EmailGenerator from '../components/AIFeatures/EmailGenerator';
import LeadQualifier from '../components/AIFeatures/LeadQualifier';
import SalesStrategy from '../components/AIFeatures/SalesStrategy';
import VehicleDescriptionGenerator from '../components/AIFeatures/VehicleDescriptionGenerator';
import FeedbackAnalyzer from '../components/AIFeatures/FeedbackAnalyzer';
import CompetitiveComparison from '../components/AIFeatures/CompetitiveComparison';
import FinancingOptions from '../components/AIFeatures/FinancingOptions';
import PersonalizedOffers from '../components/AIFeatures/PersonalizedOffers';
import CustomerInsights from '../components/AIFeatures/CustomerInsights';
import MarketTrends from '../components/AIFeatures/MarketTrends';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCpu } = FiIcons;

function AITools() {
  // Sample data for components
  const sampleCustomer = {
    firstName: 'John',
    lastName: 'Doe',
    email: 'john@example.com',
    phone: '(555) 123-4567',
    interestedVehicle: '2024 Honda CR-V',
    status: 'active',
    budget: 35000
  };

  const sampleLead = {
    firstName: 'Sarah',
    lastName: 'Johnson',
    email: 'sarah@example.com',
    interestedVehicle: '2024 Tesla Model Y',
    budget: 55000,
    source: 'Website'
  };

  const sampleVehicle = {
    year: 2024,
    make: 'Honda',
    model: 'CR-V',
    type: 'SUV',
    color: 'Pearl White',
    price: 35000
  };

  const sampleInteractions = [
    {
      type: 'call',
      notes: 'Discussed Honda CR-V features and financing options',
      timestamp: new Date().toISOString()
    },
    {
      type: 'email',
      notes: 'Sent brochure with detailed specifications',
      timestamp: new Date(Date.now() - 86400000).toISOString()
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            <SafeIcon icon={FiCpu} className="w-6 h-6 text-primary-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">AI Tools</h1>
            <p className="text-sm text-gray-500">Enhance your workflow with AI-powered features</p>
          </div>
        </div>
      </div>

      {/* Communication Tools */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Communication Tools</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <EmailGenerator customer={sampleCustomer} />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <FeedbackAnalyzer />
          </motion.div>
        </div>
      </div>

      {/* Sales Tools */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Sales Tools</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <LeadQualifier lead={sampleLead} />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <SalesStrategy customer={sampleCustomer} />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <CompetitiveComparison vehicle={sampleVehicle} />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <FinancingOptions vehicle={sampleVehicle} customer={sampleCustomer} />
          </motion.div>
        </div>
      </div>

      {/* Vehicle Tools */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Vehicle Tools</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <VehicleDescriptionGenerator vehicle={sampleVehicle} />
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <MarketTrends />
          </motion.div>
        </div>
      </div>

      {/* Customer Tools */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Customer Tools</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
          >
            <CustomerInsights customer={sampleCustomer} interactions={sampleInteractions} />
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0 }}
          >
            <PersonalizedOffers customer={sampleCustomer} />
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export default AITools;